import { StrictMode, useEffect, useState, type ReactElement } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { loadConfig } from "./config.js";
import { AuthProvider, useAuth } from "./auth/AuthContext.js";
import { createApiClient, ApiError } from "./api/client.js";
import { PortalProvider, type PortalState } from "./state/PortalContext.js";
import { AppLayout } from "./layout/AppLayout.js";
import { ModulePage } from "./pages/ModulePage.js";
import { LinkingPage } from "./pages/LinkingPage.js";
import { FeaturePage } from "./pages/FeaturePage.js";
import { ModelsPage } from "./pages/ModelsPage.js";
import { IntelligencePage } from "./pages/IntelligencePage.js";
import { AdminsPage } from "./pages/AdminsPage.js";
import type { RuntimeConfig, Catalog, Me } from "./types.js";
import "./styles.css";

function RootRedirect({ catalog }: { catalog: Catalog }): ReactElement {
  const first = catalog[0];
  if (!first || !first.stages[0]) return <p>No services available for your account.</p>;
  const stage = first.stages[0];
  const mod = stage.modules[0]?.id ?? "_link";
  return <Navigate to={`/${first.serviceId}/${stage.id}/${mod}`} replace />;
}

function AuthedApp({ config }: { config: RuntimeConfig }): ReactElement {
  const { accessToken, sessionExpired } = useAuth();
  const [state, setState] = useState<PortalState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const api = createApiClient({ apiBase: config.apiBase, getToken: () => accessToken });
    let active = true;
    async function load(): Promise<void> {
      try {
        const [me, catalog] = await Promise.all([api.me(), api.catalog()]);
        if (active) {
          setState({
            api,
            me: me as Me,
            catalog: catalog as Catalog,
            productName: config.branding?.productName ?? config.productName,
            branding: config.branding ?? { productName: config.productName },
            reloadCatalog: async () => {
              const c = await api.catalog();
              setState((prev) => (prev ? { ...prev, catalog: c } : prev));
            },
          });
        }
      } catch (e) {
        if (!active) return;
        // Expired/invalid token: refresh silently or return to Sign in instead of showing an error.
        if (e instanceof ApiError && e.status === 401) {
          void sessionExpired();
          return;
        }
        setError((e instanceof Error && e.message) || "Failed to load portal");
      }
    }
    void load();
    return () => {
      active = false;
    };
  }, [accessToken, config.apiBase]);

  if (error) return <p role="alert">{error}</p>;
  if (!state) return <p role="status">Loading…</p>;

  return (
    <PortalProvider value={state}>
      <BrowserRouter basename={config.basePath && config.basePath !== "/" ? config.basePath.replace(/\/$/, "") : undefined}>
        <Routes>
          <Route element={<AppLayout />}>
            <Route path=":service/:stage/_link" element={<LinkingPage />} />
            <Route path=":service/:stage/_models" element={<ModelsPage />} />
            <Route path=":service/:stage/_intelligence" element={<IntelligencePage />} />
            <Route path=":service/:stage/f/:feature" element={<FeaturePage />} />
            <Route path=":service/:stage/:module" element={<ModulePage />} />
            <Route path="_admins" element={<AdminsPage />} />
          </Route>
          <Route path="/" element={<RootRedirect catalog={state.catalog} />} />
          <Route path="*" element={<RootRedirect catalog={state.catalog} />} />
        </Routes>
      </BrowserRouter>
    </PortalProvider>
  );
}

function Gate({ config }: { config: RuntimeConfig }): ReactElement {
  const { user, loading, login } = useAuth();
  useEffect(() => {
    if (config.productName) document.title = config.productName;
  }, [config.productName]);
  if (loading) return <p role="status">Signing in…</p>;
  if (!user) {
    return (
      <main className="signin">
        <h1>{config.productName}</h1>
        <button type="button" onClick={() => void login()}>
          Sign in
        </button>
      </main>
    );
  }
  return <AuthedApp config={config} />;
}

async function bootstrap(): Promise<void> {
  const root = createRoot(document.getElementById("root")!);
  try {
    const config = await loadConfig();
    root.render(
      <StrictMode>
        <AuthProvider config={config}>
          <Gate config={config} />
        </AuthProvider>
      </StrictMode>
    );
  } catch (e) {
    root.render(<p role="alert">Failed to load configuration: {e instanceof Error ? e.message : "unknown error"}</p>);
  }
}

void bootstrap();
